import { dateFromString, formatDecimalHours, isoWeek } from '@coneno/c-sheet-shared'
import type { TimeBucket } from '@/lib/report-summary'
import { cn } from '@/lib/utils'
import { ChartTooltip } from './chart-tooltip'
import { useChartHover } from './use-chart-hover'

type TimeBarChartProps = {
  unit: 'day' | 'week'
  buckets: TimeBucket[]
}

const dayFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
})
const weekdayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short' })

const shortDateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })

/** Up to a month every day is labelled; longer ranges label the start of each week. */
const maxLabelledDays = 31

function bucketTitle(unit: 'day' | 'week', bucket: TimeBucket): string {
  const date = dateFromString(bucket.start)
  return unit === 'day'
    ? dayFormat.format(date)
    : `Week ${isoWeek(bucket.start)} (from ${shortDateFormat.format(date)})`
}

function bucketLabel(unit: 'day' | 'week', bucket: TimeBucket, count: number): string {
  const date = dateFromString(bucket.start)
  if (unit === 'week' || count > maxLabelledDays) return shortDateFormat.format(date)
  return count <= 7 ? `${weekdayFormat.format(date)} ${date.getDate()}` : String(date.getDate())
}

// A month or less labels every day. Longer daily ranges label Mondays; weekly bars label their
// start date, thinned out to about a dozen labels.
function showLabel(
  unit: 'day' | 'week',
  bucket: TimeBucket,
  index: number,
  count: number
): boolean {
  if (unit === 'week') return index % Math.ceil(count / 12) === 0
  if (count <= maxLabelledDays) return true
  return dateFromString(bucket.start).getDay() === 1
}

/** Bars of hours per day, or per week for long ranges. */
export function TimeBarChart({ unit, buckets }: TimeBarChartProps): React.JSX.Element {
  const maxMinutes = Math.max(0, ...buckets.map((bucket) => bucket.minutes))
  const scaleHours = Math.max(1, Math.ceil(maxMinutes / 60))
  const scaleMinutes = scaleHours * 60
  const { hover, track, clear } = useChartHover()

  return (
    <figure
      data-chart=""
      className="relative m-0 flex min-w-0 flex-1 flex-col"
      aria-label={`Hours per ${unit}`}
      onMouseLeave={clear}
    >
      <div className="flex gap-2">
        <div className="flex h-32 w-8 flex-col justify-between text-right text-[10px] text-muted-foreground tabular-nums">
          <span>{scaleHours} h</span>
          <span>0</span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="relative flex h-32 items-end gap-px border-b">
            <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed" />
            <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed" />
            {buckets.map((bucket) => {
              const weekend =
                unit === 'day' && [0, 6].includes(dateFromString(bucket.start).getDay())
              return (
                <div
                  key={bucket.start}
                  onMouseMove={(event) =>
                    track(
                      event,
                      bucket.start,
                      bucketTitle(unit, bucket),
                      `${formatDecimalHours(bucket.minutes)} h`
                    )
                  }
                  className={cn(
                    'flex h-full min-w-0 flex-1 flex-col justify-end rounded-t-sm',
                    weekend && 'bg-muted/40',
                    hover?.key === bucket.start && 'bg-muted'
                  )}
                >
                  <div
                    className="rounded-t-sm bg-primary"
                    style={{ height: `${(bucket.minutes / scaleMinutes) * 100}%` }}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex gap-px text-[10px] text-muted-foreground">
            {buckets.map((bucket, index) => (
              <span key={bucket.start} className="min-w-0 flex-1 text-center whitespace-nowrap">
                {showLabel(unit, bucket, index, buckets.length)
                  ? bucketLabel(unit, bucket, buckets.length)
                  : ''}
              </span>
            ))}
          </div>
        </div>
      </div>
      <ChartTooltip hover={hover} />
    </figure>
  )
}
